/**
 * @file validators.js
 * @description Built-in validators and validator combinators
 * @version 1.0.0
 */

import { getNestedValue } from './utils';

// ============================================================================
// Built-in Validators
// ============================================================================

/**
 * Built-in validators for common validation scenarios
 *
 * @namespace validators
 */
export const validators = {
  /**
   * Validates that a field has a value
   *
   * @param {string} [message='This field is required'] - Custom error message
   * @returns {Function} Validator function
   *
   * @example
   * validators.required('Email is required')
   */
  required(message = 'This field is required') {
    return value => {
      if (value === null || value === undefined || value === '') {
        return message;
      }
      // Handle lookup fields
      if (typeof value === 'object' && value !== null && 'id' in value) {
        return value.id ? true : message;
      }
      return true;
    };
  },

  /**
   * Validates minimum string length
   * @param {number} min - Minimum length
   * @param {string} [message] - Custom error message
   * @returns {Function} Validator function
   */
  minLength(min, message) {
    return value => {
      if (!value) return true;
      const msg = message || `Must be at least ${min} characters`;
      return value.length >= min ? true : msg;
    };
  },

  /**
   * Validates maximum string length
   * @param {number} max - Maximum length
   * @param {string} [message] - Custom error message
   * @returns {Function} Validator function
   */
  maxLength(max, message) {
    return value => {
      if (!value) return true;
      const msg = message || `Must not exceed ${max} characters`;
      return value.length <= max ? true : msg;
    };
  },

  /**
   * Validates email format
   * @param {string} [message='Please enter a valid email'] - Custom error message
   * @returns {Function} Validator function
   */
  email(message = 'Please enter a valid email') {
    return value => {
      if (!value) return true;
      const emailRegex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/;
      return emailRegex.test(value) ? true : message;
    };
  },

  /**
   * Validates numeric value (allows decimals)
   * @param {string} [message='Must be a valid number'] - Custom error message
   * @returns {Function} Validator function
   */
  number(message = 'Must be a valid number') {
    return value => {
      if (!value && value !== 0) return true;
      return !Number.isNaN(Number(value)) ? true : message;
    };
  },

  /**
   * Validates integer value (no decimals)
   * @param {string} [message='Must be a whole number'] - Custom error message
   * @returns {Function} Validator function
   */
  integer(message = 'Must be a whole number') {
    return value => {
      if (!value && value !== 0) return true;
      const num = Number(value);
      return Number.isInteger(num) ? true : message;
    };
  },

  /**
   * Validates URL format
   * @param {string} [message='Must be a valid URL'] - Custom error message
   * @returns {Function} Validator function
   */
  url(message = 'Must be a valid URL') {
    return value => {
      if (!value) return true;
      try {
        const url = new URL(value);
        return url ? true : message;
      } catch {
        return message;
      }
    };
  },

  /**
   * Validates boolean must be true (for checkboxes)
   * Useful for terms & conditions, agreements, etc.
   * @param {string} [message='You must agree to continue'] - Custom error message
   * @returns {Function} Validator function
   */
  boolean(message = 'You must agree to continue') {
    return value => {
      return value === true ? true : message;
    };
  },

  /**
   * Validates minimum numeric value
   * @param {number} minimum - Minimum value
   * @param {string} [message] - Custom error message
   * @returns {Function} Validator function
   */
  min(minimum, message) {
    return value => {
      if (!value && value !== 0) return true;
      const num = Number(value);
      const msg = message || `Must be at least ${minimum}`;
      return num >= minimum ? true : msg;
    };
  },

  /**
   * Validates maximum numeric value
   * @param {number} maximum - Maximum value
   * @param {string} [message] - Custom error message
   * @returns {Function} Validator function
   */
  max(maximum, message) {
    return value => {
      if (!value && value !== 0) return true;
      const num = Number(value);
      const msg = message || `Must not exceed ${maximum}`;
      return num <= maximum ? true : msg;
    };
  },

  /**
   * Validates against regex pattern
   * @param {RegExp} regex - Regular expression pattern
   * @param {string} [message='Invalid format'] - Custom error message
   * @returns {Function} Validator function
   */
  pattern(regex, message = 'Invalid format') {
    return value => {
      if (!value) return true;
      return regex.test(value) ? true : message;
    };
  },

  /**
   * Validates that field matches another field's value
   * Useful for password confirmation, email confirmation, etc.
   * @param {string} fieldPath - Path to field to match
   * @param {string} [message='Fields do not match'] - Custom error message
   * @returns {Function} Validator function
   */
  matchesField(fieldPath, message = 'Fields do not match') {
    return (value, form) => {
      if (!value) return true;

      const targetValue = getNestedValue(form, fieldPath);
      return value === targetValue ? true : message;
    };
  },

  /**
   * Custom validator with full context access
   * @param {Function} validatorFn - Validation function
   * @param {string} [message='Invalid value'] - Custom error message
   * @returns {Function} Validator function
   */
  custom(validatorFn, message = 'Invalid value') {
    return async (value, form, context) => {
      if (!value && value !== 0 && value !== false) return true;
      try {
        const result = await validatorFn(value, form, context);
        return result ? true : message;
      } catch (error) {
        console.warn('Custom validation failed:', error);
        return message;
      }
    };
  },
};

// ============================================================================
// Validator Combinators
// ============================================================================

/**
 * Combines multiple validators with AND logic
 * All validators must pass for validation to succeed
 * Returns first error message encountered
 *
 * @param {...Function} validatorFns - Validator functions to combine
 * @returns {Function} Combined validator function
 *
 * @example
 * all(
 *   validators.required('Email is required'),
 *   validators.email('Invalid email format')
 * )
 */
export function all(...validatorFns) {
  return async (value, form, context) => {
    for (const validator of validatorFns) {
      const result = await validator(value, form, context);
      if (result !== true) {
        return result;
      }
    }
    return true;
  };
}

/**
 * Conditional validator - only validates if condition is met
 *
 * @param {Function} conditionFn - Condition function that returns boolean
 * @param {Function} validator - Validator to apply when condition is true
 * @returns {Function} Conditional validator function
 *
 * @example
 * when(
 *   (form) => form.customerType === 'company',
 *   validators.required('Tax number required for companies')
 * )
 */
export function when(conditionFn, validator) {
  return (value, form, context) => {
    if (conditionFn(form, context)) {
      return validator(value, form, context);
    }
    return true;
  };
}

/**
 * Sequential validator - ensures dependencies are valid first
 * Only validates if all dependency fields are valid
 *
 * @param {string[]} dependencies - Array of field paths that must be valid first
 * @param {Function} validator - Validator function to apply
 * @param {string} [pendingMessage='Please complete previous fields first'] - Message while waiting
 * @returns {Function} Sequential validator function
 * @example
 * sequential(
 *   ['schedule.planned_date'], // Dependencies
 *   validators.custom((value, form) => {
 *     return new Date(value) >= new Date(form.schedule.planned_date);
 *   }, 'Must be after planned date'),
 *   'Please set planned date first' // Pending message
 * )
 */
export function sequential(
  dependencies,
  validator,
  pendingMessage = 'Please complete previous fields first'
) {
  return (value, form, context) => {
    if (!context?.isSequentialValidation) {
      return validator(value, form, context);
    }

    // Check if all dependencies are valid
    for (const dep of dependencies) {
      if (!context.isFieldValid(dep)) {
        return pendingMessage;
      }
    }

    return validator(value, form, context);
  };
}
